// Public Terms of Service / Privacy Policy pages. Routed at /terms and
// /privacy, OUTSIDE the access-code gate, so the agreement screen's hard
// "I agree" gate can link to the real documents (and so anyone can read
// them without an invite). Both reuse the landing chrome.
//
// Plain-language, early-access-appropriate. The privacy copy MUST stay accurate to
// the deployed server (see docs/topics/pilot/participant-consent.md):
//   - messages parsed in-browser, sent to Anthropic, written server-side only
//     while the run executes, then auto-deleted the moment the analysis finishes
//     (chain_runner purges corpus_root once the encrypted bundle is persisted)
//   - Anthropic: 30-day commercial-terms deletion (NOT "Zero Data Retention",
//     enterprise-only and Aurora doesn't hold), never used to train, flagged-
//     message exception
//   - the finished analysis: a 7-day encrypted come-back copy, kept by default,
//     keyless (we never hold the passphrase).
// Don't overclaim.
//
// Re-verified for the batched-fan-out rollout (2026-06-08): both retention
// claims still hold under batching.
//   (a) Batched runs take longer (up to ~1 hr) so the corpus sits on the server
//       longer — but the purge is still tied to COMPLETION, not a clock, so the
//       duration-agnostic "deleted the moment it finishes" wording stays exact.
//   (b) Anthropic's Batches API holds the (corpus-bearing) request up to ~29
//       days — that is INSIDE the "within 30 days" commercial-terms deletion we
//       already state, so the user-facing claim is unchanged (and we don't name
//       "Batches API" in user copy — no tech jargon).

import type { ReactNode } from "react";
import { CloudMast } from "./CloudMast";

const EFFECTIVE = "Effective July 3, 2026";
const CONTACT = "terms@plaintosee.cc";
// Payment/refund mail goes to the monitored billing alias, not the general
// terms address — chargeback + refund deadlines are time-sensitive.
const BILLING = "billing@plaintosee.cc";

type Kind = "terms" | "privacy";

function Chrome({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="landing legal-page">
      <div className="landing-wrap">
        <CloudMast
          right={
            <a className="legal-back" href="/">
              ← Back to Aurora
            </a>
          }
        />

        <article className="legal-article">
          <div className="landing-eyebrow">{EFFECTIVE}</div>
          <h1 className="landing-title">{title}</h1>
          {children}
        </article>

        <footer className="landing-foot">
          <div className="landing-foot-left">
            Aurora · A product of Plain to See LLC.
          </div>
          <div className="landing-foot-right">&copy; 2026 Aurora</div>
        </footer>
      </div>
    </main>
  );
}

function PrivacyBody() {
  return (
    <Chrome title="Privacy Policy">
      <p className="legal-lede">
        Aurora is a product of Plain to See LLC, in early access. This
        notice explains what we collect, what happens to your messages, and
        what we don't do. If anything here is unclear, email{" "}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>The email you give us</strong>, so we can reach you about your
          analysis — for example, a note when it's ready (so you don't have to
          watch it run), a re-download link if you ask for one, or a note
          afterward to ask how it went.
        </li>
        <li>
          <strong>Operational metadata about each run</strong> — when you ran an
          analysis, how long it took, what it cost, and which export format you
          used. Never your message bodies, never the parties' names.
        </li>
        <li>
          <strong>Your access code</strong>, to confirm your access and keep
          each code to a single analysis.
        </li>
      </ul>

      <h2>What happens to your messages</h2>
      <p>
        Your export is read <strong>in your browser</strong> first. To run the
        analysis, your messages are sent to Anthropic's Claude and written to
        Aurora's server while the analysis runs. The moment it finishes and your
        encrypted result is ready, your messages and the working files are{" "}
        <strong>deleted from our servers automatically</strong>. We don't keep
        your messages after that.
      </p>

      <h2>How Anthropic handles your messages</h2>
      <p>
        To run the analysis, your messages go to Anthropic's Claude. Anthropic
        handles that data under <strong>its own terms, which we don't
        control</strong>. Under those terms, Anthropic{" "}
        <strong>
          automatically deletes your messages and the results within 30 days
        </strong>{" "}
        and never uses them to train its models. In the rare case a message is
        flagged by Anthropic's automated safety systems, it may be kept longer
        for review.
      </p>

      <h2>When you pay</h2>
      <p>
        If your analysis is paid for, the payment is handled by{" "}
        <strong>Stripe</strong>, a payment processor. You enter your card details
        on Stripe's checkout, not on Aurora — <strong>we never see or store your
        card number</strong>. To process the payment, Stripe collects your email,
        the name and billing address on the card, your card details, and your
        device's network address, and keeps a record of the transaction for
        several years to meet its own legal and anti-fraud obligations.
      </p>
      <p>
        What <em>Aurora</em> keeps about a payment is small: your access code, a
        payment receipt number from Stripe, the date, and the analysis size and
        price you paid. <strong>No name, no card, no billing address.</strong> If
        you ask us to delete what we hold, we can — but we can't reach the records
        Stripe keeps under its own obligations. On your bank statement the charge
        appears as <strong>PLAINTOSEE.CC</strong>.
      </p>
      <h2>Your result file</h2>
      <p>
        The analysis output is an encrypted HTML file you download. It's locked
        with a passphrase shown to you once, when you start the analysis. We
        use that passphrase to lock your file, then discard it — so we can't
        open your file, and we can't recover the passphrase if you lose it.
        You open the file in any browser, offline, and keep it as long as you
        like.
      </p>
      <p>
        Your locked file stays on our servers for up to <strong>7 days</strong>{" "}
        so the link in your "analysis ready" email works — from this device or
        another — then it's deleted. The file is locked with{" "}
        <strong>your passphrase, which we don't keep</strong>, so even we
        can't open it.
      </p>

      <h2>What we don't do</h2>
      <ul>
        <li>Aurora staff don't browse your case content.</li>
        <li>We don't sell your information or share it for advertising.</li>
        <li>
          We don't keep your messages after your analysis finishes, and we never
          use your case to train AI.
        </li>
      </ul>

      <h2>Retention and your choices</h2>
      <p>
        We keep your email and the operational metadata above for as long as
        you're using Aurora. You can ask us to access or delete what we hold on
        our own servers at any time — email{" "}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. What our deletion can't
        reach are records other companies hold under their own terms — and we
        don't control theirs: the copy Anthropic keeps for up to 30 days to run
        the analysis (see <em>How Anthropic handles your messages</em>), and the
        payment records Stripe keeps for its legal obligations (see{" "}
        <em>When you pay</em>).
      </p>

      <h2>Changes</h2>
      <p>
        We may update this notice as Aurora evolves. Material changes will be
        reflected by the effective date above.
      </p>
    </Chrome>
  );
}

function TermsBody() {
  return (
    <Chrome title="Terms of Service">
      <p className="legal-lede">
        These terms govern your use of Aurora, an early-access product. By
        entering your access code and agreeing, you accept these terms.
        Questions: <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
      </p>

      <h2>What Aurora is</h2>
      <p>
        Aurora reads family-law message exports and surfaces the patterns the
        coercive-control literature names, citing the specific messages each
        pattern came from. It produces a court-prep evidence packet for you to
        review.
      </p>

      <h2>Aurora is not legal advice</h2>
      <p>
        Aurora names patterns and cites messages. It does not render verdicts,
        reach legal conclusions, or give legal advice, and it is not a
        substitute for a lawyer. Review every citation yourself before relying
        on anything Aurora produces, and consult a qualified attorney about your
        situation. Aurora will not fabricate patterns, and it is not guaranteed
        to find every pattern present — a pattern Aurora doesn't surface is not
        proof that none exists.
      </p>

      <h2>Access and eligibility</h2>
      <p>
        Access to Aurora is granted by access code — whether you purchased it or
        received it from a funding partner. Your code is for your own use and is
        good for a single analysis, start to finish. Don't share it.
      </p>

      <h2>Acceptable use</h2>
      <p>
        Only analyze records you have a legal right to use. Don't use Aurora for
        any unlawful purpose, and don't attempt to disrupt or reverse-engineer
        the service.
      </p>

      <h2>Payment and refunds</h2>
      <p>
        Some analyses are paid; others are covered by a funding partner, in which
        case your access code already carries the cost and you pay nothing. When
        there is a charge, it's a price on the pay-what-you-can spectrum, shown
        before you pay, and you pay per analysis — there is no subscription.
        Payment is processed by Stripe; the{" "}
        <a href="/privacy">Privacy Policy</a> explains what that involves.
      </p>
      <p>
        <strong>Refunds: ask within 7 days.</strong> If a paid run didn't
        finish, or the result didn't serve you, email{" "}
        <a href={`mailto:${BILLING}`}>{BILLING}</a> within 7 days and we'll
        refund you — no form, no fight. We run this on good faith, and we ask
        the same in return. (A record too large for Aurora to analyze in one
        pass is caught before you pay — so there's nothing to charge or refund.)
      </p>

      <h2>Your content</h2>
      <p>
        You keep ownership of your messages and your finished analysis. The
        result file is locked with a passphrase <strong>only you keep</strong> —
        we discard our copy the moment your file is locked with it, so if you
        lose it we cannot recover it for you, and without it the file can't be
        opened. You're responsible for safeguarding it. Your locked file stays
        re-downloadable for 7 days, then it's deleted — see the{" "}
        <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>No warranty</h2>
      <p>
        Aurora is early-access software, provided "as is" and "as available,"
        without warranties of any kind. It may contain errors and may change or
        be unavailable from time to time.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, we are not liable for any
        indirect, incidental, or consequential damages arising from your use of
        Aurora.
      </p>

      <h2>Changes and termination</h2>
      <p>
        We may update these terms, or change or discontinue Aurora, at any time.
        Continued use after a change means you accept the updated terms.
      </p>
    </Chrome>
  );
}

export function LegalPage({ kind }: { kind: Kind }) {
  return kind === "privacy" ? <PrivacyBody /> : <TermsBody />;
}
